import * as Dialog from '@radix-ui/react-dialog';
import {
  EXPORT_FIELDS,
  type ExportFieldGroup,
  type ExportGrain,
} from '@uit/shared/schemas/resource-export';
import { useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth.js';
import s from './ExportResourcesDialog.module.scss';

export interface ExportResult {
  filename: string;
  rows: number;
  columns: number;
  withRates: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceTotal: number;
  onExported: (result: ExportResult) => void;
}

const GROUP_LABELS: Record<ExportFieldGroup, string> = {
  resource: 'Resource',
  vendor: 'Vendor',
  engagement: 'Engagement & rates',
};
const GROUP_ORDER: ExportFieldGroup[] = ['resource', 'vendor', 'engagement'];

function parseFilename(header: string | null, fallback: string): string {
  const m = header?.match(/filename="?([^"]+)"?/);
  return m?.[1] ?? fallback;
}

function triggerDownload(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ExportResourcesDialog({ open, onOpenChange, resourceTotal, onExported }: Props) {
  const { admin } = useAuth();
  // The whole export is owner-only (T-186), so a caller who reaches this dialog
  // is an owner and every field is available. Kept as a guard rather than
  // dropped so a future non-owner entry point degrades to a rate-free export
  // instead of a 403-on-download.
  const isOwner = !!admin?.is_owner;

  const fields = useMemo(() => EXPORT_FIELDS.filter((f) => isOwner || !f.gated), [isOwner]);

  const [grain, setGrain] = useState<ExportGrain>('resource');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(fields.filter((f) => f.defaultOn || f.required).map((f) => f.key)),
  );
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function selectGroup(group: ExportFieldGroup): void {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const f of fields) if (f.group === group) next.add(f.key);
      return next;
    });
  }

  const columns = selected.size;

  async function doExport(): Promise<void> {
    setExporting(true);
    setError(null);
    // Preserve catalog order in the column list.
    const ordered = fields.filter((f) => selected.has(f.key)).map((f) => f.key);
    const url = `/api/resources/export.csv?grain=${grain}&fields=${ordered.join(',')}`;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}`);
      const csv = await res.text();
      const filename = parseFilename(res.headers.get('content-disposition'), 'resources.csv');
      triggerDownload(filename, csv);
      const rows = csv.trim() === '' ? 0 : csv.trim().split('\n').length - 1;
      onExported({
        filename,
        rows,
        columns: ordered.length,
        withRates: ordered.some((k) => k.startsWith('rate_')),
      });
      onOpenChange(false);
    } catch {
      setError('Could not export. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>Export resources to CSV</Dialog.Title>
          <Dialog.Description className={s.description}>
            Always all resources (ignores the page + filters). Choose the row grain and which fields
            to include.
          </Dialog.Description>

          {error && (
            <div className={s.formError} role="alert">
              {error}
            </div>
          )}

          <div className={s.grain}>
            {(['resource', 'engagement'] as const).map((g) => (
              <label key={g} className={grain === g ? s.grainSel : s.grainOpt}>
                <input
                  type="radio"
                  name="grain"
                  checked={grain === g}
                  onChange={() => setGrain(g)}
                />
                <span>
                  <span className={s.grainTitle}>
                    {g === 'resource' ? 'One row per resource' : 'One row per engagement'}
                  </span>
                  <span className={s.grainSub}>
                    {g === 'resource'
                      ? "latest engagement's fields"
                      : "every engagement's rates"}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className={s.groups}>
            {GROUP_ORDER.map((group) => {
              const groupFields = fields.filter((f) => f.group === group);
              if (groupFields.length === 0) return null;
              return (
                <div key={group} className={s.group}>
                  <div className={s.groupHead}>
                    <span className={s.groupName}>{GROUP_LABELS[group]}</span>
                    <button type="button" className={s.selectAll} onClick={() => selectGroup(group)}>
                      All
                    </button>
                  </div>
                  <div className={s.checks}>
                    {groupFields.map((f) => (
                      <label
                        key={f.key}
                        className={f.required ? `${s.check} ${s.checkLocked}` : s.check}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(f.key)}
                          disabled={f.required}
                          onChange={() => toggle(f.key)}
                        />
                        <code>{f.key}</code>
                        {f.required && <span className={s.tagReq}>always</span>}
                        {f.gated && <span className={s.tagGated}>admin</span>}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <p className={s.exclNote}>
            Sensitive vendor fields — <code>EIN</code> and <code>ACH</code> — are never exported.
            {isOwner && ' This export is owner-only.'}
          </p>

          <div className={s.footer}>
            <span className={s.count}>
              <b>{resourceTotal}</b> resources · <b>{columns}</b> columns
            </span>
            <span className={s.actions}>
              <button type="button" className={s.cancel} onClick={() => onOpenChange(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={s.submit}
                disabled={exporting || columns === 0}
                onClick={() => {
                  void doExport();
                }}
              >
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
